import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, IconButton, CircularProgress, InputBase } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import { useSite } from '../context/SiteContext';
import useAuthGuard from '../hooks/useAuthGuard';
import usePageReady from '../hooks/usePageReady';
import api from '../services/api';

// ─── Layout constants ─────────────────────────────────────────────────────────
const NAVBAR_H = 72;
const PB       = 50;
const PX       = 0;
const GAP      = 30; // px fallback — overridden by hall.gridGap

const BoothClipDef = () => (
  <svg width="0" height="0" style={{ position: 'absolute', overflow: 'hidden' }}>
    <defs>
      <clipPath id="booth-clip" clipPathUnits="objectBoundingBox">
        <path d="M0 0.9 Q0 1 0.1 1 L0.9 1 Q1 1 1 0.9 L0.88 0.1 Q0.88 0 0.78 0 L0.22 0 Q0.12 0 0.12 0.1 Z" />
      </clipPath>
    </defs>
  </svg>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const INTRO_KEY   = (id) => `govirtual_exhibition_intro_${id}`;
const hasSeenIntro  = (id) => { try { return !!localStorage.getItem(INTRO_KEY(id)); } catch { return false; } };
const markIntroSeen = (id) => { try { localStorage.setItem(INTRO_KEY(id), '1'); } catch {} };

// ─── Intro Modal ──────────────────────────────────────────────────────────────
const IntroModal = ({ slides, primary, onClose }) => {
  const [slide, setSlide] = useState(0);
  const sorted  = [...slides].sort((a, b) => a.order - b.order);
  const current = sorted[slide];
  const isLast  = slide === sorted.length - 1;

  if (!current) { onClose(); return null; }

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 1400, bgcolor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Box sx={{ width: '100%', height: '100%', maxWidth: 970, maxHeight: 550, bgcolor: '#fff', borderRadius: 4, overflow: 'hidden', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, boxShadow: '0 24px 80px rgba(0,0,0,0.3)', position: 'relative' }}>
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2, bgcolor: 'rgba(255,255,255,0.9)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: '0 0 44%', minHeight: { xs: 220, md: 0 }, bgcolor: '#0D1B2A', position: 'relative', overflow: 'hidden' }}>
          {current.mediaUrl && (current.mediaType === 'video'
            ? <Box component="video" src={current.mediaUrl} autoPlay muted loop playsInline sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', position: 'absolute', inset: 0 }} />
            : <Box component="img"  src={current.mediaUrl} alt={current.title} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', position: 'absolute', inset: 0 }} />
          )}
        </Box>
        <Box sx={{ flex: 1, p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <Box>
            {current.badge && (
              <Box sx={{ display: 'inline-flex', alignSelf: 'flex-start', bgcolor: `${primary}15`, color: primary, fontSize: 11, fontWeight: 700, letterSpacing: 1, px: 1.5, py: 0.4, borderRadius: 100, mb: 2, textTransform: 'uppercase' }}>
                {current.badge}
              </Box>
            )}
            <Typography variant="h3" sx={{ mb: 1.5, color: primary, lineHeight: 1.2 }}>{current.title}</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3.5, lineHeight: 1.7 }}>{current.description}</Typography>
          </Box>
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => { if (current.ctaUrl) window.open(current.ctaUrl, '_blank'); if (isLast) onClose(); else setSlide(s => s + 1); }}
              sx={{ bgcolor: primary, borderRadius: 100, px: 3, fontWeight: 700, '&:hover': { bgcolor: primary, filter: 'brightness(0.9)' } }}
            >
              {current.ctaLabel || (isLast ? 'Get Started' : 'Next')}
            </Button>
            {sorted.length > 1 && (
              <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                {sorted.map((_, i) => (
                  <Box key={i} onClick={() => setSlide(i)} sx={{ width: i === slide ? 24 : 8, height: 8, borderRadius: 100, bgcolor: i === slide ? primary : '#CBD5E1', cursor: 'pointer', transition: 'all 0.2s' }} />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Booth Card ───────────────────────────────────────────────────────────────
const BoothCard = ({ exhibitor, primary, onClick, variant = 'desktop' }) => {
  const [hovered, setHovered] = useState(false);
  const boothColor = exhibitor.boothColor || '#1a1a2e';
  const isDesktop  = variant === 'desktop';
  const cardRef    = useRef(null);

  return (
    <Box
      ref={cardRef}
      onClick={() => onClick(exhibitor, cardRef.current)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        cursor: 'pointer',
        transition: 'transform 0.2s ease',
        transform: hovered ? 'scale(0.95)' : 'scale(1)',
        clipPath: 'url(#booth-clip)',
        overflow: 'hidden',
        ...(isDesktop && { width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }),
      }}
    >
      <Box sx={{ overflow: 'hidden', bgcolor: '#fff', ...(isDesktop && { flex: 1, display: 'flex', flexDirection: 'column' }) }}>
        <Box sx={{
          position: 'relative',
          bgcolor: boothColor,
          ...(isDesktop ? { flex: 1 } : { width: '100%', paddingBottom: '72%' }),
        }}>
          {exhibitor.boothThumbnail ? (
            <Box component="img" src={exhibitor.boothThumbnail} alt={exhibitor.name}
              sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.3s ease' }}
            />
          ) : (
            <Box sx={{
              position: 'absolute', inset: 0,
              background: `linear-gradient(135deg, ${primary}30 0%, ${boothColor} 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Typography sx={{ fontSize: 40, opacity: 0.3 }}>🏢</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{
          px: 1.5, py: 1.25,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: '#fff',
          ...(isDesktop ? { flexShrink: 0, height: 44 } : { minHeight: 44 }),
        }}>
          {exhibitor.logoUrl ? (
            <Box component="img" src={exhibitor.logoUrl} alt={exhibitor.name}
              sx={{ maxHeight: 28, maxWidth: '80%', objectFit: 'contain', display: 'block' }} />
          ) : (
            <Typography variant="caption" fontWeight={700} sx={{ color: primary, fontSize: 12, textAlign: 'center', px: 1 }}>
              {exhibitor.name}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// ─── Desktop Carousel ─────────────────────────────────────────────────────────
const DesktopCarousel = ({ exhibitors, primary, onCardClick, gridCols = 5, gridRows = 3, gridGap = GAP }) => {
  const [swiper, setSwiper]           = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [cellSize, setCellSize]       = useState(null);
  const [cols, setCols]               = useState(gridCols);
  const slideRef = useRef(null);

  const perPage    = cols * gridRows;
  const pages      = [];
  for (let i = 0; i < exhibitors.length; i += perPage) {
    pages.push(exhibitors.slice(i, i + perPage));
  }
  const totalPages = pages.length;

  useEffect(() => { swiper?.slideTo(0, 0); }, [exhibitors, swiper]);

  useEffect(() => {
    const compute = () => {
      if (!slideRef.current) return;
      const { clientWidth: w, clientHeight: h } = slideRef.current;

      // Respect the hall's configured column count but still clamp for narrow viewports
      const maxCols = gridCols;
      const activeCols =
        w >= 1280 ? maxCols :
        w >= 1024 ? Math.min(maxCols, 4) :
        w >= 768  ? Math.min(maxCols, 3) : Math.min(maxCols, 2);

      setCols(activeCols);

      const byWidth  = (w - (activeCols  - 1) * gridGap) / activeCols;
      const byHeight = (h - (gridRows - 1) * gridGap) / gridRows;
      setCellSize(Math.floor(Math.min(byWidth, byHeight)));
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (slideRef.current) ro.observe(slideRef.current);
    return () => ro.disconnect();
  }, [gridCols, gridRows, gridGap]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>

      {/* Left arrow */}
      <Box onClick={() => swiper?.slidePrev()} sx={{
        position: 'fixed', left: 16, top: '50%', transform: 'translateY(-50%)',
        zIndex: 200, width: 44, height: 44, borderRadius: '50%',
        bgcolor: activeIndex === 0 ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.9)',
        border: '1px solid', borderColor: activeIndex === 0 ? 'rgba(255,255,255,0.2)' : '#E2E8F0',
        boxShadow: activeIndex === 0 ? 'none' : '0 4px 16px rgba(0,0,0,0.12)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: activeIndex === 0 ? 'default' : 'pointer', transition: 'all 0.2s',
        '&:hover': activeIndex > 0 ? { bgcolor: '#fff', boxShadow: '0 6px 20px rgba(0,0,0,0.18)' } : {},
      }}>
        <ArrowBackIosNewIcon sx={{ fontSize: 16, color: activeIndex === 0 ? 'rgba(255,255,255,0.4)' : '#64748B' }} />
      </Box>

      <Swiper
        modules={[Navigation, Pagination]}
        onSwiper={setSwiper}
        onSlideChange={(s) => setActiveIndex(s.activeIndex)}
        speed={450}
        style={{ width: '100%', height: '100%' }}
      >
        {pages.map((pageItems, pageIdx) => (
          <SwiperSlide
            key={pageIdx}
            style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Box
              ref={pageIdx === 0 ? slideRef : undefined}
              sx={{ px: 5, maxWidth: 1200, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingX: 5 }}
            >
              {cellSize && (
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`,
                  gridTemplateRows:    `repeat(${gridRows}, ${cellSize}px)`,
                  justifyContent: 'space-between',
                  rowGap: `${gridGap}px`,
                  width: '100%',
                }}>
                  {pageItems.map(ex => (
                    <Box key={ex._id} sx={{ width: cellSize, height: cellSize, overflow: 'hidden' }}>
                      <BoothCard exhibitor={ex} primary={primary} onClick={(ex, el) => onCardClick(ex, el)} />
                    </Box>
                  ))}
                  {Array.from({ length: Math.max(0, perPage - pageItems.length) }).map((_, i) => (
                    <Box key={`ph-${i}`} sx={{ width: cellSize, height: cellSize }} />
                  ))}
                </Box>
              )}
            </Box>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Right arrow */}
      <Box onClick={() => swiper?.slideNext()} sx={{
        position: 'fixed', right: 16, top: '50%', transform: 'translateY(-50%)',
        zIndex: 200, width: 44, height: 44, borderRadius: '50%',
        bgcolor: activeIndex === totalPages - 1 ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.9)',
        border: '1px solid', borderColor: activeIndex === totalPages - 1 ? 'rgba(255,255,255,0.2)' : '#E2E8F0',
        boxShadow: activeIndex === totalPages - 1 ? 'none' : '0 4px 16px rgba(0,0,0,0.12)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: activeIndex === totalPages - 1 ? 'default' : 'pointer', transition: 'all 0.2s',
        '&:hover': activeIndex < totalPages - 1 ? { bgcolor: '#fff', boxShadow: '0 6px 20px rgba(0,0,0,0.18)' } : {},
      }}>
        <ArrowForwardIosIcon sx={{ fontSize: 16, color: activeIndex === totalPages - 1 ? 'rgba(255,255,255,0.4)' : '#64748B' }} />
      </Box>

      {/* Dot indicators */}
      {totalPages > 1 && (
        <Box sx={{ position: 'absolute', bottom: -40, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 1, pb: 1, zIndex: 10 }}>
          {Array.from({ length: totalPages }).map((_, i) => (
            <Box key={i} onClick={() => swiper?.slideTo(i)} sx={{
              width: i === activeIndex ? 24 : 8, height: 8, borderRadius: 100,
              bgcolor: i === activeIndex ? primary : 'rgba(255,255,255,0.5)',
              cursor: 'pointer', transition: 'all 0.25s',
            }} />
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─── Mobile Grid ──────────────────────────────────────────────────────────────
const MobileGrid = ({ exhibitors, primary, onCardClick }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
    {exhibitors.map(ex => (
      <BoothCard key={ex._id} exhibitor={ex} primary={primary} variant="mobile" onClick={(ex, el) => onCardClick(ex, el)} />
    ))}
  </Box>
);

// ─── Search Bar ───────────────────────────────────────────────────────────────
const SearchBar = ({ value, onChange, primary }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center',
    bgcolor: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(8px)',
    borderRadius: 100,
    px: 2, py: 0.75,
    maxWidth: 1200,
    width: { xs: '100%', md: 320 },
    border: '1px solid', borderColor: 'rgba(255,255,255,0.6)',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    transition: 'box-shadow 0.2s, border-color 0.2s',
    '&:focus-within': { boxShadow: `0 2px 16px rgba(0,0,0,0.14)`, borderColor: primary },
  }}>
    <SearchIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1, flexShrink: 0 }} />
    <InputBase
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder="Search exhibitors…"
      sx={{ flex: 1, fontSize: 13, '& input': { p: 0 } }}
    />
    {value && (
      <IconButton size="small" onClick={() => onChange('')} sx={{ p: 0.25, ml: 0.5 }}>
        <CloseIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
      </IconButton>
    )}
  </Box>
);

// ═════════════════════════════════════════════════════════════════════════════
// MAIN EXHIBITORS PAGE
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Props
 * ─────
 * exhibitionSlug  – the ExhibitionPage slug from the URL (e.g. "main-hall").
 *                   Pass null / undefined to load the event's default hall.
 * preloadedExhibitors – optional array already fetched by the resolver
 *                       (kept for backward-compat; not used when we have a hall slug).
 */
const ExhibitorsPage = ({ exhibitionSlug, preloadedExhibitors = [] }) => {
  const { event, startTransitionRef, afterTransitionRef, isTransitioning } = useSite();
  const navigate = useNavigate();
  const branding = event?.branding || {};
  const primary  = branding.primaryColor || '#1565C0';

  const { authChecked } = useAuthGuard(event);
  usePageReady();

  // ── Exhibition hall state ──────────────────────────────────────────────────
  const [hall, setHall]           = useState(null);   // ExhibitionPage document
  const [exhibitors, setExhibitors] = useState(preloadedExhibitors);
  const [loading, setLoading]     = useState(true);
  const [showIntro, setShowIntro] = useState(false);
  const [search, setSearch]       = useState('');

  const filtered = search.trim()
    ? exhibitors.filter(ex => ex.name?.toLowerCase().includes(search.toLowerCase()))
    : exhibitors;

  // ── Fetch hall config + scoped exhibitors ──────────────────────────────────
  useEffect(() => {
    if (!event?._id) return;

    let cancelled = false;
    setLoading(true);
    setExhibitors([]);
    setHall(null);

    const loadHall = async () => {
      try {
        // 1. Load the ExhibitionPage document
        //    - with a slug  → /api/exhibition/public/:eventId/:slug
        //    - without slug → /api/exhibition/public/:eventId  (default hall)
        const hallRes = exhibitionSlug
          ? await api.get(`/exhibition/public/${event._id}/${exhibitionSlug}`)
          : await api.get(`/exhibition/public/${event._id}`);

        const hallData = hallRes.data.data;
        if (cancelled) return;
        setHall(hallData);

        // 2. Load exhibitors scoped to this hall
        //    Always use the slug-based exhibitors endpoint so the backend
        //    applies its 3-tier strategy (assigned → pinned → unassigned).
        const slug = hallData.slug;
        const exRes = await api.get(`/exhibition/public/${event._id}/${slug}/exhibitors`);
        if (cancelled) return;
        setExhibitors(exRes.data.data || []);
      } catch (err) {
        // If the ExhibitionPage API 404s (old event without hall documents),
        // fall back to the legacy exhibitors endpoint.
        if (!cancelled && !exhibitionSlug) {
          try {
            const fallback = await api.get(`/exhibitors/public/${event._id}`);
            if (!cancelled) setExhibitors(fallback.data.data || []);
          } catch {
            // silent
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadHall();
    return () => { cancelled = true; };
  }, [event?._id, exhibitionSlug]);

  // ── Show intro modal (using hall's introSlides, not lobby's) ──────────────
  useEffect(() => {
    if (!hall) return;
    if (!hall.showIntro || !hall.introSlides?.length) return;
    if (hasSeenIntro(hall._id)) return;

    if (isTransitioning && afterTransitionRef) {
      afterTransitionRef.current = () => setShowIntro(true);
    } else {
      setShowIntro(true);
    }
  }, [hall]);

  const handleCloseIntro = () => {
    if (hall) markIntroSeen(hall._id);
    setShowIntro(false);
  };

  // ── Navigate into a booth ─────────────────────────────────────────────────
  const handleCardClick = (exhibitor, cardEl) => {
    const path = event?.urlMode === 'flat'
      ? `/${exhibitor.slug}`
      : `/${event.slug}/${exhibitor.slug}`;

    if (exhibitor.boothThumbnail && exhibitor.transitionVideoUrl && cardEl && startTransitionRef?.current) {
      const imgEl = cardEl.querySelector('img') || cardEl;
      const rect  = imgEl.getBoundingClientRect();
      startTransitionRef.current({ srcRect: rect, thumbnailUrl: exhibitor.boothThumbnail, videoUrl: exhibitor.transitionVideoUrl, path });
    } else {
      navigate(path);
    }
  };

  // ── Background from ExhibitionPage ────────────────────────────────────────
  const bgSx = (() => {
    if (!hall) return { bgcolor: '#D1D9E0' };
    if (hall.bgType === 'image' && hall.bgUrl) {
      return { backgroundImage: `url(${hall.bgUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' };
    }
    if (hall.bgType === 'video') {
      // video bg handled separately below
      return { bgcolor: hall.bgColor || '#D1D9E0' };
    }
    return { bgcolor: hall.bgColor || '#D1D9E0' };
  })();

  const hasBg      = hall?.bgType === 'image' && !!hall?.bgUrl;
  const hasVideoBg = hall?.bgType === 'video' && !!hall?.bgUrl;

  // Grid settings from hall (with safe fallbacks)
  const gridCols = hall?.gridCols ?? 5;
  const gridRows = hall?.gridRows ?? 3;
  const gridGap  = hall?.gridGap  ?? GAP;

  const introSlides = hall?.introSlides || [];

  if (!authChecked) return null;

  // Shared empty / no-results UI
  const emptyState = (
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
      <Typography sx={{ fontSize: 40 }}>🔍</Typography>
      <Typography variant="h6" color="text.secondary">
        {search ? `No results for "${search}"` : 'No exhibitors yet'}
      </Typography>
    </Box>
  );

  return (
    <>
      <BoothClipDef />

      {/* ── Desktop ── */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
        position: 'relative',
        ...bgSx,
      }}>
        {/* Video background */}
        {hasVideoBg && (
          <Box component="video" src={hall.bgUrl} autoPlay muted loop playsInline
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
          />
        )}

        <Box sx={{ position: 'absolute', inset: 0, bgcolor: hasBg || hasVideoBg ? 'rgba(0,0,0,0.08)' : 'rgba(209,217,224,0.95)', zIndex: 0 }} />

        <Box sx={{
          position: 'relative', zIndex: 1,
          display: 'flex', flexDirection: 'column',
          height: '100%',
          pt: `${NAVBAR_H}px`, pb: `${PB}px`, px: `${PX}px`,
        }}>
          {/* Search bar */}
          <Box sx={{ width: '100%', my: 2, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <SearchBar value={search} onChange={setSearch} primary={primary} />
          </Box>

          {loading ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress sx={{ color: primary }} />
            </Box>
          ) : filtered.length === 0 ? (
            emptyState
          ) : (
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <DesktopCarousel
                exhibitors={filtered}
                primary={primary}
                onCardClick={handleCardClick}
                gridCols={gridCols}
                gridRows={gridRows}
                gridGap={gridGap}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Mobile ── */}
      <Box sx={{
        display: { xs: 'block', md: 'none' },
        minHeight: '100vh',
        position: 'relative',
        ...bgSx,
      }}>
        {hasVideoBg && (
          <Box component="video" src={hall.bgUrl} autoPlay muted loop playsInline
            sx={{ position: 'fixed', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
          />
        )}
        <Box sx={{ position: 'absolute', inset: 0, bgcolor: hasBg || hasVideoBg ? 'rgba(0,0,0,0.08)' : 'rgba(209,217,224,0.95)', zIndex: 0 }} />
        <Box sx={{ position: 'relative', zIndex: 1, pt: '80px', pb: 4, px: 2 }}>
          <SearchBar value={search} onChange={setSearch} primary={primary} />
          <Box sx={{ mt: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
                <CircularProgress sx={{ color: primary }} />
              </Box>
            ) : filtered.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 6, gap: 1 }}>
                <Typography sx={{ fontSize: 36 }}>🔍</Typography>
                <Typography variant="body1" color="text.secondary">
                  {search ? `No results for "${search}"` : 'No exhibitors yet'}
                </Typography>
              </Box>
            ) : (
              <MobileGrid exhibitors={filtered} primary={primary} onCardClick={handleCardClick} />
            )}
          </Box>
        </Box>
      </Box>

      {showIntro && introSlides.length > 0 && (
        <IntroModal slides={introSlides} primary={primary} onClose={handleCloseIntro} />
      )}
    </>
  );
};

export default ExhibitorsPage;